import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Label, Select, Stack, Textarea } from '@easymoving/ui';
import type { Box, BoxDraft, Destination, ProgressStatus } from '@easymoving/types';
import { useBackend } from '../../lib/backend/context';
import { useCustomRooms } from '../../hooks/useUserData';
import { destinationOptions, roomOptions, statusOptions } from '../../lib/taxonomy/options';

const NONE = '__none__';

export function BoxFormDialog({
  open,
  onOpenChange,
  initial,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial?: Box;
  onSaved?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const backend = useBackend();
  const customRooms = useCustomRooms();

  const [name, setName] = useState(initial?.name ?? '');
  const [room, setRoom] = useState(initial?.room ?? '');
  const [destination, setDestination] = useState<string>(initial?.destination ?? NONE);
  const [status, setStatus] = useState<string>(initial?.progressStatus ?? NONE);
  const [storageLocation, setStorageLocation] = useState(initial?.storageLocation ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState(false);

  async function submit() {
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    const draft: BoxDraft = {
      name: name.trim(),
      room: room || undefined,
      destination: destination === NONE ? undefined : (destination as Destination),
      progressStatus: status === NONE ? undefined : (status as ProgressStatus),
      storageLocation: storageLocation || undefined,
      notes: notes.trim() || undefined,
    };
    setBusy(true);
    try {
      const id = initial
        ? (await backend.updateBox(initial.id, draft), initial.id)
        : await backend.createBox(draft);
      onSaved?.(id);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={initial ? t('boxes.edit') : t('boxes.create')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('boxes.cancel')}
          </Button>
          <Button onClick={submit} disabled={busy}>
            {t('boxes.save')}
          </Button>
        </div>
      }
    >
      <Stack gap="sm">
        <Label required>
          {t('boxes.name')}
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.trim()) setNameError(false);
            }}
            placeholder={t('boxes.namePlaceholder')}
            aria-invalid={nameError}
            autoFocus
          />
          {nameError && <span className="text-[12px] text-clay">{t('auth.errors.required')}</span>}
        </Label>
        <div className="grid grid-cols-2 gap-3">
          <Label>
            {t('boxes.room')}
            <Select
              ariaLabel={t('boxes.room')}
              value={room || NONE}
              placeholder={t('item.selectPlaceholder')}
              options={[{ value: NONE, label: '—' }, ...roomOptions(t, customRooms)]}
              onValueChange={(v) => setRoom(v === NONE ? '' : v)}
            />
          </Label>
          <Label>
            {t('boxes.destination')}
            <Select
              ariaLabel={t('boxes.destination')}
              value={destination}
              options={[{ value: NONE, label: '—' }, ...destinationOptions(t)]}
              onValueChange={setDestination}
            />
          </Label>
          <Label>
            {t('boxes.status')}
            <Select
              ariaLabel={t('boxes.status')}
              value={status}
              options={[{ value: NONE, label: '—' }, ...statusOptions(t)]}
              onValueChange={setStatus}
            />
          </Label>
          <Label>
            {t('boxes.storageLocation')}
            <Input value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)} />
          </Label>
        </div>
        <Label>
          {t('boxes.notes')}
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </Label>
      </Stack>
    </Dialog>
  );
}
